import {EngineConfig_Visualization} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import {FieldLabel, flp} from "../../../@Shared/FieldLabel.js";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {CheckBox, Column, Row, RowLR, Spinner, Text} from "react-vcomponents";
import {TextPlus} from "web-vcore";
import {ControlInput} from "../../../@Shared/ControlInput.js";
import {PhaseSelectionUI} from "../../../@Shared/PhaseSelectionUI.js";
import {EngineConfigPanels_SharedProps, ExtendEngineConfigPanelSharedProps} from "../ConfigPanel_Local.js";
import {PromptPoolsUI} from "./Visualization/PromptPoolUI.js";
import {visComp_firstPromptDelay} from "../../../../Engine/LiveSession/Components/VisualizationComp.js";

export type SharedProps_VisualizationConfigUI = {enabled?: boolean, config: EngineConfig_Visualization, ChangeConfig: (mutator: (config: EngineConfig_Visualization)=>any)=>void};

const GetSharedProps = (props: EngineConfigPanels_SharedProps)=>ExtendEngineConfigPanelSharedProps(props, c=>c.visualization, (c, v)=>c.visualization = v);

const splitAt = 230;

export const Visualization_ConfigUI = observer_mgl((props: {remote?: boolean} & EngineConfigPanels_SharedProps)=>{
	const {enabled} = props;
	const sharedProps = GetSharedProps(props);
	const {config, ChangeConfig} = sharedProps;
	const fp = (fieldName: keyof typeof config)=>`visualization.${fieldName}`;

	return (
		<Column mt={15} style={{background: "hsla(0,0%,100%,.1)", borderRadius: 5, padding: 5}}>
			<Row style={{fontSize: 18}}>
				<CheckBox text="Visualization" enabled={enabled} value={config.enabled} onChange={val=>ChangeConfig(c=>c.enabled = val)}/>
			</Row>

			{config.enabled &&
			<>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Start force-enabled:" fieldPath={fp("startForceEnable_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.startForceEnable_triggerSet} onChange={val=>ChangeConfig(c=>c.startForceEnable_triggerSet = val)}/>
					<TextPlus ml={10} info={`
						When set, this overrides the "Force-enabled expire after" setting for this specific trigger.
					`.AsMultiline(0)}>Expire:</TextPlus>
					<Spinner ml={5} min={0} step={1} value={config.startForceEnable_expireAfterOverride ?? undefined} placeholder={`${config.forceEnabled_expireAfter}`}
						onChange={val=>ChangeConfig(c=>c.startForceEnable_expireAfterOverride = (val as number|n))}/>
					<Text>m</Text>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Stop force-enabled:" fieldPath={fp("stopForceEnable_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.stopForceEnable_triggerSet} onChange={val=>ChangeConfig(c=>c.stopForceEnable_triggerSet = val)}/>
					<CheckBox ml={10} text="Silently" value={config.stopForceEnabled_silently} onChange={val=>ChangeConfig(c=>c.stopForceEnabled_silently = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Toggle force-enabled:" fieldPath={fp("toggleForceEnable_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.toggleForceEnable_triggerSet} onChange={val=>ChangeConfig(c=>c.toggleForceEnable_triggerSet = val)}/>
					<TextPlus ml={10} info={`
						When set, this overrides the "Force-enabled expire after" setting for this specific trigger. (when it force-enables)
					`.AsMultiline(0)}>Expire:</TextPlus>
					<Spinner ml={5} min={0} step={1} value={config.toggleForceEnable_expireAfterOverride ?? undefined} placeholder={`${config.forceEnabled_expireAfter}`}
						onChange={val=>ChangeConfig(c=>c.toggleForceEnable_expireAfterOverride = (val as number|n))}/>
					<Text>m</Text>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Next prompt-pool:" fieldPath={fp("nextPromptPool_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.nextPromptPool_triggerSet} onChange={val=>ChangeConfig(c=>c.nextPromptPool_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Increase target grid generation:" fieldPath={fp("increaseTargetGridGen_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.increaseTargetGridGen_triggerSet} onChange={val=>ChangeConfig(c=>c.increaseTargetGridGen_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Decrease target grid generation:" fieldPath={fp("decreaseTargetGridGen_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.decreaseTargetGridGen_triggerSet} onChange={val=>ChangeConfig(c=>c.decreaseTargetGridGen_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Increase max grid gen-distance:" fieldPath={fp("increaseMaxGridGenDistance_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.increaseMaxGridGenDistance_triggerSet} onChange={val=>ChangeConfig(c=>c.increaseMaxGridGenDistance_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Decrease max grid gen-distance:" fieldPath={fp("decreaseMaxGridGenDistance_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.decreaseMaxGridGenDistance_triggerSet} onChange={val=>ChangeConfig(c=>c.decreaseMaxGridGenDistance_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Increase prompt interval:" fieldPath={fp("increaseInterval_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.increaseInterval_triggerSet} onChange={val=>ChangeConfig(c=>c.increaseInterval_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Decrease prompt interval:" fieldPath={fp("decreaseInterval_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.decreaseInterval_triggerSet} onChange={val=>ChangeConfig(c=>c.decreaseInterval_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Increase revolution durations:" fieldPath={fp("increaseRevolutionDurations_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.increaseRevolutionDurations_triggerSet} onChange={val=>ChangeConfig(c=>c.increaseRevolutionDurations_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Decrease revolution durations:" fieldPath={fp("decreaseRevolutionDurations_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.decreaseRevolutionDurations_triggerSet} onChange={val=>ChangeConfig(c=>c.decreaseRevolutionDurations_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text p={flp}>Base-enabled, unless (any):</Text>
					<CheckBox text="Micro-snoozing" value={config.promptingBaseEnabledUnless_microSnoozeActive} onChange={val=>ChangeConfig(c=>c.promptingBaseEnabledUnless_microSnoozeActive = val)}/>
					<CheckBox ml={5} text="Media->play" value={config.promptingBaseEnabledUnless_mediaCompPlaying} onChange={val=>ChangeConfig(c=>c.promptingBaseEnabledUnless_mediaCompPlaying = val)}/>
					<Text ml={10}>In phase:</Text>
					<PhaseSelectionUI value={config.promptingBaseEnabledUnless_inPhases ?? []} onChange={val=>ChangeConfig(c=>c.promptingBaseEnabledUnless_inPhases = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Force-enabled expire after:" fieldPath={fp("forceEnabled_expireAfter")} info={`
						How many minutes after force-enabling before the force-enabled state automatically turns off. (if <= 0, feature disabled)
						Note: Force-enabled also automatically "expires" when the number of headphones/earbuds connected drops to 0.
					`.AsMultiline(0)}/>
					<Spinner min={0} step={1} value={config.forceEnabled_expireAfter} onChange={val=>ChangeConfig(c=>c.forceEnabled_expireAfter = val)}/>
					<Text>m</Text>
					<CheckBox ml={10} text="Silently" value={config.forceEnabled_expireSilently} onChange={val=>ChangeConfig(c=>c.forceEnabled_expireSilently = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Prompt interval:" fieldPath={fp("promptInterval")} info={`
						The default interval (in seconds) at which visualization prompts will be given. Individual pools can override this with their own interval.
						Note: If using multiple pools, all should override this, with this as the "effective global prompt-interval". (ie. avg interval after applying disables, weights, etc.)
						Note: The first prompt after enabling prompting will always play ${visComp_firstPromptDelay}s after enabling, regardless of this (and pool-override) settings.
					`.AsMultiline(0)}/>
					<Spinner min={1} step="any" value={config.promptInterval} onChange={val=>ChangeConfig(c=>c.promptInterval = val)}/>
					<Text>s</Text>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Interval adjustment step:" fieldPath={fp("promptInterval_step")} info="The step-size for prompt-interval adjustment increases/decreases (from hotkeys/triggers)."/>
					<Spinner min={0} step={.1} value={config.promptInterval_step} onChange={val=>ChangeConfig(c=>c.promptInterval_step = val)}/>
					<Text>s</Text>
				</RowLR>

				<PromptPoolsUI {...sharedProps}/>
			</>}
		</Column>
	);
});